/* ============================================================================
 * custom-cx.js — harempants.com client-side customer-experience / analytics layer
 *
 * Generic, ad-block-safe name (avoids "analytics"/"tracking"/"pixel"/"ga"/"gtm"
 * substrings that some filter lists match). Home for first-party, page-context
 * behavioural signals — start with GA4 collection-page events; reuse for future
 * integrations.
 *
 * WHAT IT DOES
 * On collection pages it detects the interactions the native Google & YouTube
 * channel can't and posts them DIRECTLY to GA4 (G-NRERKNKQGE) over /g/collect,
 * reusing the native pixel's client_id + session_id (from the first-party _ga
 * cookies) so they stitch into the same session. No GTM, no second gtag config
 * — see plans/research/ga4-ads-pageview-collision-2026-06.md (the old
 * cxDataLayer→GTM path loaded a duplicate G-NRERKNKQGE config that raced the
 * native tag and suppressed ~50% of page_view/ecommerce events):
 *   • view_item_list  — the list presented. Fires on load AND re-fires (debounced)
 *                       when the list materially changes (filter / sort / show-more),
 *                       tagged with `list_trigger` and a `result_count`.
 *   • filter_applied / sort_changed / load_more — the ACT (per interaction).
 *   • select_item     — a product-card click.
 *   • no_results      — a filter combination that empties the grid.
 *   • filter_drawer_open — the mobile/tablet facet drawer is opened, timed
 *                       (`duration_ms`, click → painted — a GENERIC timing metric
 *                       grouped by eventName) + sequenced (`drawer_open_seq`) to
 *                       expose first-open (cold) lag and the "opened but never
 *                       filtered" drop-off vs filter_applied. An opt-in pre-warm
 *                       (pre-append the drawer to <body> on intent/idle) pays the
 *                       cold-open cost early for the experiment's treatment arm;
 *                       `prewarmed` is the as-treated flag (BigQuery/DebugView only).
 *   • experiment_exposure — generic A/B exposure { experiment_id, experiment_variant }
 *                       read from window.Experiments (assigned pre-paint by
 *                       snippets/custom-ab-head.liquid); one event + 3 dims serve ALL
 *                       experiments. This file only emits it; it does not bucket.
 *
 * DESIGN NOTES (first principles)
 *  - Two complementary signals per filter: `filter_applied` (the act — fires
 *    PER TOGGLE, immediately, so each filter is counted) and a re-fired
 *    `view_item_list` (the resulting list — fires ONCE, debounced, after the grid
 *    settles). They are separate pushes on purpose: debouncing is correct for the
 *    list view but would lose per-filter granularity if applied to the act.
 *  - `view_item_list` re-fire is gated on an actual interaction (`pendingTrigger`)
 *    and debounced to the settled DOM, so rapid multi-filter clicking yields ONE
 *    list-view, not many (avoids impression inflation).
 *  - The items array is CAPPED (CONFIG.maxItems) to stay well under GA4 event
 *    payload limits; for `load_more` only the newly-appended items are sent.
 *  - `applied_filters` is the sorted/canonical set of active filters so identical
 *    combinations (applied in any order) aggregate instead of fragmenting.
 *  - `list_trigger` / `result_count` / `filter_count` are CUSTOM params (not GA4
 *    standards). Register `list_trigger`+`filter_count` as custom dimensions and
 *    `result_count` as a custom metric; compute clean CTR by filtering
 *    `view_item_list` to `list_trigger = load`. (`applied_filters` is high-
 *    cardinality — emitted for DebugView/BigQuery but not registered.)
 *
 * WHY DIRECT /g/collect (not GTM): Shopify's Google & YouTube channel owns the
 * on-page Google tag for G-NRERKNKQGE. A second on-page config for the same
 * measurement ID (what our GTM container did) shares one gtag.js instance and
 * races it — suppressing page_view. Posting our events ourselves needs no config
 * at all, so it can't collide. We mirror gtag's own /g/collect encoding and carry
 * the shared identity for session stitching. Data attrs come from
 * sections/main-collection-product-grid.liquid. See analytics/README.md and
 * plans/research/ga4-ads-pageview-collision-2026-06.md.
 * ========================================================================== */
(function () {
  'use strict';

  var CONFIG = {
    version: '4.0.0',    // 4.x = direct /g/collect transport (no GTM); see file header
    maxItems: 30,        // cap items[] per view_item_list (payload safety)
    refireDebounceMs: 400, // wait for the grid to settle before re-firing view_item_list
    warmDrawer: true,    // master kill-switch for the facet-drawer pre-warm experiment
    // A/B experiments are defined ONCE in the registry in snippets/custom-ab-head.liquid
    // and assigned pre-paint into window.Experiments. This file no longer buckets — it
    // reads window.Experiments.variant(id) and emits the exposure (see below).
  };

  /* =========================================================================
   * GA4 transport — direct, configless POST to /g/collect (the endpoint gtag
   * itself uses; NO api secret, NO GTM, NO second gtag config → no collision
   * with the native Google & YouTube channel tag on G-NRERKNKQGE). Events stitch
   * into the SAME GA4 session as the native pixel by reusing its client_id +
   * session_id from the first-party _ga / _ga_<stream> cookies. The packed
   * `experiments` vector rides every event as a user-scoped property (forward-
   * only), replacing the old GTM userProperties mapping.
   * ======================================================================= */
  var MEASUREMENT_ID = 'G-NRERKNKQGE';
  var STREAM_ID = MEASUREMENT_ID.replace(/^G-/, '');
  var GA_ENDPOINT = 'https://www.google-analytics.com/g/collect';
  var _hitSeq = 0;

  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  /* Identity from the native GA cookies so our events join the same user+session:
   *   _ga          = "GA1.1.<a>.<b>"                -> client_id = "<a>.<b>"
   *   _ga_<STREAM> = "GS2.1.s<sid>$o<n>$..."  (GS2) -> session_id <sid>, number <n>
   *                  "GS1.1.<sid>.<n>.…"      (GS1 fallback)
   * Cached once both ids exist (the native gtag mints them before our 1st event). */
  var _identity = null;
  function identity() {
    if (_identity) return _identity;
    var clientId, sessionId, sessionNumber;
    var ga = readCookie('_ga');
    if (ga) { var m = ga.match(/GA\d\.\d\.(\d+\.\d+)/); clientId = m ? m[1] : ga.split('.').slice(-2).join('.'); }
    var gs = readCookie('_ga_' + STREAM_ID);
    if (gs) {
      var parts = gs.split('.'), payload = parts[2] || '';
      var sidM = payload.match(/^s?(\d+)/); sessionId = sidM ? sidM[1] : payload;
      var nM = payload.match(/\$o(\d+)/); sessionNumber = nM ? nM[1] : (parts[3] || '1');
    }
    var id = { clientId: clientId, sessionId: sessionId, sessionNumber: sessionNumber };
    if (clientId && sessionId) _identity = id;
    return id;
  }

  /* gtag-compatible item encoding: prN = tilde-delimited 2-char-coded fields. */
  function encodeItems(qp, items) {
    (items || []).forEach(function (it, i) {
      var f = [];
      if (it.item_id != null && it.item_id !== '') f.push('id' + it.item_id);
      if (it.item_name != null && it.item_name !== '') f.push('nm' + it.item_name);
      if (it.item_brand) f.push('br' + it.item_brand);
      if (it.item_category) f.push('ca' + it.item_category);
      if (it.index != null) f.push('lp' + it.index);
      if (it.price != null) f.push('pr' + it.price);
      if (it.quantity != null) f.push('qt' + it.quantity);
      qp['pr' + (i + 1)] = f.join('~');
    });
  }

  /* Build + POST one event (identity already resolved). */
  function _post(eventName, params, items, id) {
    try {
      var qp = {
        v: '2', tid: MEASUREMENT_ID, gtm: 'cxjs' + CONFIG.version,
        _p: String(Date.now()), cid: id.clientId,
        sid: id.sessionId || String(Math.floor(Date.now() / 1000)),
        sct: id.sessionNumber || '1', seg: '1',
        dl: location.href, dr: document.referrer, dt: document.title,
        en: eventName, _et: '1', _s: String(++_hitSeq),
      };
      var packed = (window.Experiments && window.Experiments.packed) ? window.Experiments.packed() : '';
      if (packed) qp['up.experiments'] = packed; // user-scoped, forward-only
      if (params) for (var k in params) {
        if (!params.hasOwnProperty(k) || k === 'experiments') continue; // experiments is user-scoped (above)
        var val = params[k];
        if (val == null) continue; // drop only null/undefined — keep '' and 0 (parity with the GTM tag)
        if (typeof val === 'number') qp['epn.' + k] = String(val);
        else if (typeof val === 'boolean') qp['ep.' + k] = val ? 'true' : 'false';
        else qp['ep.' + k] = String(val);
      }
      encodeItems(qp, items);
      var url = GA_ENDPOINT + '?' + Object.keys(qp).map(function (k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(qp[k]);
      }).join('&');
      if (navigator.sendBeacon) navigator.sendBeacon(url);
      else fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors' });
    } catch (e) { /* never break the page */ }
  }

  /* Send an event, BUFFERING until the native gtag has minted the _ga cookies, so
   * every event stitches into the real session instead of fragmenting under a
   * generated id. Our collection events can fire before the async native pixel
   * sets the cookie, so we queue + flush rather than drop. Give up after ~12s
   * (cookie never came → consent-denied or the native pixel is blocked). */
  var _pending = [], _flushTimer = null;
  function _stopFlush() { if (_flushTimer) { window.clearInterval(_flushTimer); _flushTimer = null; } }
  function _flush() {
    var id = identity();
    if (!id.clientId) return false;
    var q = _pending; _pending = [];
    for (var i = 0; i < q.length; i++) _post(q[i].en, q[i].params, q[i].items, id);
    _stopFlush();
    return true;
  }
  function send(eventName, params, items) {
    var id = identity();
    if (id.clientId) { _post(eventName, params, items, id); return; }
    _pending.push({ en: eventName, params: params, items: items });
    if (!_flushTimer) {
      var tries = 0;
      _flushTimer = window.setInterval(function () {
        if (_flush()) return;
        if (++tries > 40) { _stopFlush(); _pending = []; } // ~12s give-up
      }, 300);
    }
  }

  /* Public emit API — SAME signatures as before; route to send() (was cxDataLayer).
   * list id/name + cx_version travel as event params on every event, exactly as the
   * GTM Data Layer Variables did. `extra` adds custom params (list_trigger, etc.). */
  function pushEcommerce(eventName, listId, listName, items, extra) {
    var params = { item_list_id: listId, item_list_name: listName, cx_version: CONFIG.version };
    if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) params[k] = extra[k];
    send(eventName, params, items);
  }
  function pushEvent(eventName, params) {
    var p = { cx_version: CONFIG.version };
    if (params) for (var k in params) if (params.hasOwnProperty(k)) p[k] = params[k];
    send(eventName, p, null);
  }

  /* =========================================================================
   * Experiment REPORTING — installs the reporting half onto window.Experiments.
   *
   * The ASSIGNMENT half (registry, deterministic multivariate bucketing, sticky
   * persistence, pre-paint html.ab-<id>--<variant> classes, and the packed
   * `experiments` vector) is built SYNCHRONOUSLY in snippets/custom-ab-head.liquid.
   * This file does NOT bucket — it READS the assignments and emits the GA4 exposure
   * via send() (the /g/collect transport above), carrying `experiments` as a
   * user-scoped property on this and every other cx event.
   *
   * One generic `experiment_exposure` event + 3 GA4 dims serve ALL experiments:
   * experiment_id + experiment_variant (event-scoped) and experiments (user-scoped,
   * the packed assignment vector). See plans/plan-ab-framework-canonical.md.
   * ======================================================================= */
  var Experiments = window.Experiments || null;
  var _exposed = {};
  function exposeExperiment(id) {
    if (!Experiments || _exposed[id]) return;
    if (window.Shopify && window.Shopify.designMode) return; // never pollute experiments in the theme editor
    var variant = Experiments.variant(id);
    if (variant == null) return;            // disabled / not enrolled → never expose
    _exposed[id] = true;
    pushEvent('experiment_exposure', {
      experiment_id: id,
      experiment_variant: variant,
      experiments: Experiments.packed ? Experiments.packed() : '',
    });
  }
  if (Experiments) {
    Experiments.track = function (name, params) { pushEvent(name, params); };
    Experiments.expose = exposeExperiment;  // replace the buffered head stub…
    var _q = Experiments._exposeQueue || []; // …and drain anything queued before this loaded
    for (var _i = 0; _i < _q.length; _i++) exposeExperiment(_q[_i]);
    _q.length = 0;
  }
  function variantFor(id) { return Experiments ? Experiments.variant(id) : null; }

  /* =========================================================================
   * Helpers
   * ======================================================================= */
  function getList() {
    var grid = document.getElementById('ProductsList');
    if (!grid) return null;
    return {
      item_list_id: grid.getAttribute('data-ga4-list-id') || '',
      item_list_name: grid.getAttribute('data-ga4-list-name') || '',
    };
  }

  function itemFromColumn(col) {
    if (!col) return null;
    var price = parseFloat(col.getAttribute('data-ga4-item-price'));
    var index = parseInt(col.getAttribute('data-ga4-item-index'), 10);
    var item = {
      item_id: col.getAttribute('data-ga4-item-id') || '',
      item_name: col.getAttribute('data-ga4-item-name') || '',
    };
    if (!isNaN(price)) item.price = price;
    if (!isNaN(index)) item.index = index;
    // item_category (= product.type) and item_brand (= product.vendor) mirror what
    // Shopify's native GA4 sends downstream, so list events tie to the funnel.
    var category = col.getAttribute('data-ga4-item-category');
    var brand = col.getAttribute('data-ga4-item-brand');
    if (category) item.item_category = category;
    if (brand) item.item_brand = brand;
    return item;
  }

  function columns() { return document.querySelectorAll('#ProductsList .product-column'); }

  /* Active filters, read from the facet form's checked inputs (the URL lags the
   * change event), sorted into a canonical string so identical combinations
   * aggregate regardless of the order they were applied. */
  function activeFilters() {
    var nodes = document.querySelectorAll('form[is="facet-form"] [name^="filter."]');
    var pairs = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var name = el.getAttribute('name');
      if (!name || name === 'sort_by') continue;
      var on = (el.type === 'checkbox' || el.type === 'radio') ? el.checked : !!el.value;
      if (!on) continue;
      pairs.push(name + '=' + (el.value || ''));
    }
    pairs.sort();
    return { applied_filters: pairs.join('&'), filter_count: pairs.length };
  }

  /* =========================================================================
   * view_item_list — fires on load and re-fires (debounced) on list changes.
   * ======================================================================= */
  var lastListCount = 0; // total products at the last view_item_list fire

  function fireViewItemList(trigger) {
    var list = getList();
    if (!list) return;
    var cols = columns();
    var total = cols.length;
    if (total === 0) return; // empty list → handled by no_results

    // load_more appends: send only the newly-revealed items; everything else is a
    // fresh list → send from the top. Cap to maxItems either way.
    var start = trigger === 'load_more' ? lastListCount : 0;
    if (start < 0 || start >= total) start = 0;
    var items = [];
    for (var i = start; i < total && items.length < CONFIG.maxItems; i++) {
      var it = itemFromColumn(cols[i]);
      if (it) items.push(it);
    }
    lastListCount = total;
    if (!items.length) return;

    var af = activeFilters();
    pushEcommerce('view_item_list', list.item_list_id, list.item_list_name, items, {
      list_trigger: trigger,
      result_count: total,
      filter_count: af.filter_count,
      applied_filters: af.applied_filters,
    });
  }

  /* =========================================================================
   * Interaction handlers (the "act" events — immediate, per interaction)
   * ======================================================================= */
  var pendingTrigger = null; // why the next grid-settle should re-fire view_item_list

  function onClick(event) {
    var list = getList();
    if (!list) return;

    var link = event.target.closest('#ProductsList .product-column a[href]');
    if (link) {
      var item = itemFromColumn(link.closest('.product-column'));
      if (item) {
        var af = activeFilters();
        pushEcommerce('select_item', list.item_list_id, list.item_list_name, [item], {
          filter_count: af.filter_count,
          applied_filters: af.applied_filters,
        });
      }
      return;
    }

    var loadMore = event.target.closest('button[is="load-more-button"]');
    if (loadMore) {
      var page = null;
      var m = (loadMore.getAttribute('action') || '').match(/[?&]page=(\d+)/);
      if (m) page = parseInt(m[1], 10);
      pushEvent('load_more', {
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        page_number: page,
      });
      pendingTrigger = 'load_more'; // grid will append → re-fire view_item_list on settle
    }
  }

  function onChange(event) {
    var list = getList();
    if (!list) return;
    var target = event.target;
    if (!target) return;

    if (target.matches && target.matches('select[is="facet-short"], select[name="sort_by"]')) {
      pushEvent('sort_changed', {
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        sort_order: target.value || '',
      });
      pendingTrigger = 'sort';
      return;
    }

    var inFacetForm = target.closest && target.closest('form[is="facet-form"]');
    var name = target.getAttribute && target.getAttribute('name');
    if (inFacetForm && name && name.indexOf('filter.') === 0 && name !== 'sort_by') {
      var applying = (target.type === 'checkbox' || target.type === 'radio') ? target.checked : !!target.value;
      pendingTrigger = 'filter'; // re-fire the resulting list whether applied or removed
      if (!applying) return;     // but only log the ACT when a filter is being applied
      var af = activeFilters();
      pushEvent('filter_applied', {
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        filter_type: name,
        filter_value: target.value || '',
        filter_count: af.filter_count,
        applied_filters: af.applied_filters,
      });
    }
  }

  /* no_results — grid re-renders via AJAX on filter, so watch for the empty state.
   * Emptiness is measured by `.product-card` (the universal Hyper card, present on
   * BOTH the collection and search grids) — NOT `.product-column`, which is the
   * collection-only wrapper carrying the GA4 data-attrs (see columns()). The search
   * template renders bare `.product-card`s with no `.product-column`, so counting
   * `.product-column` here mis-read every populated search page as empty and fired a
   * false no_results on every search-with-results pageview. (A genuinely empty search
   * drops #ProductsList entirely, so it bails at the guard above — as before.) */
  var lastEmpty = null;
  function checkNoResults() {
    var grid = document.getElementById('ProductsList');
    var list = getList();
    if (!grid || !list) return;
    var empty = grid.querySelectorAll('.product-card').length === 0;
    if (empty === lastEmpty) return;
    lastEmpty = empty;
    if (empty) {
      var af = activeFilters();
      pushEvent('no_results', {
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        filter_count: af.filter_count,
        applied_filters: af.applied_filters,
      });
    }
  }

  /* Grid settled after an interaction → update no_results state and, if a tracked
   * interaction caused it, re-fire view_item_list for the new list. */
  function onGridSettled() {
    checkNoResults();
    if (!pendingTrigger) return;        // only re-fire after a real filter/sort/show-more
    var trigger = pendingTrigger;
    pendingTrigger = null;
    if (columns().length === 0) return; // empty → no_results already covered it
    fireViewItemList(trigger);
  }

  /* =========================================================================
   * filter_drawer_open — the facet drawer is opened (mobile/tablet "Filter").
   *
   * There is NO server fetch on open: the facets are server-rendered INLINE
   * (snippets/facets-drawer.liquid → `{% render 'facets' %}`), so opening is a
   * pure client-side reveal (theme.js sets `hidden = false` on the drawer). We
   * still time it — `filter_drawer_open_ms`, click → drawer painted — because
   * revealing a large hidden facet subtree forces a one-time layout/paint (and
   * image decode) that janks the FIRST open on slow mobile CPUs, then is warm on
   * subsequent opens. `drawer_open_seq` (1,2,3… per pageview) lets GA4 segment
   * the cold first-open from warm re-opens. Pairs with `filter_applied` to
   * measure "opened the filter but never actually filtered".
   *
   * Timing note: measured to the frame AFTER the browser reveals + paints the
   * drawer (double rAF), which isolates the reveal/layout cost — the part a user
   * feels as lag — from the cosmetic CSS open animation that follows.
   * ======================================================================= */
  var drawerOpenSeq = 0;
  function nowMs() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
  function raf(cb) { return (window.requestAnimationFrame || function (f) { return window.setTimeout(f, 16); })(cb); }

  function onFacetDrawerOpenClick(event) {
    var t = event.target;
    if (!t || !t.closest) return;
    if (!t.closest('[aria-controls="FacetDrawer"]')) return;
    var drawer = document.getElementById('FacetDrawer');
    if (!drawer || drawer.hasAttribute('open')) return; // only clicks that OPEN the drawer

    var t0 = nowMs();
    var seq = ++drawerOpenSeq;
    // prewarmed = the pre-warm already reparented the drawer to <body> before this
    // (capture-phase) click — i.e. the open will skip the slow reparent. This is the
    // true "as-treated warmed" state; it correctly counts same-tap intent warms that
    // a lead-time gate would miss. (Capture runs before the theme's open handler, so
    // parent===body here means WE moved it, not the open.)
    var prewarmed = drawer.parentElement === document.body;
    raf(function () {
      raf(function () {
        if (!drawer.hasAttribute('open')) return; // open didn't actually happen → skip
        var list = getList() || { item_list_id: '', item_list_name: '' };
        var af = activeFilters();
        pushEvent('filter_drawer_open', {
          item_list_id: list.item_list_id,
          item_list_name: list.item_list_name,
          duration_ms: Math.round(nowMs() - t0), // generic, reusable timing metric (group by eventName)
          drawer_open_seq: seq,
          prewarmed: prewarmed,                  // as-treated; emit for BigQuery/DebugView, don't register
          filter_count: af.filter_count,
          applied_filters: af.applied_filters,
        });
      });
    });
  }

  /* =========================================================================
   * Drawer pre-warm (experiment) — pay the cold first-open cost EARLY/cheaply.
   *
   * Root cause (profiled, 6× CPU throttle): the slow first open is NOT images
   * (the facets are plain text) and NOT the subtree's layout/paint — it's the
   * theme moving the whole ~400-node drawer to <body> on first open
   * (requiresBodyAppended, theme.js: `document.body.append(this)`), done
   * SYNCHRONOUSLY in the click handler while the main thread is busy post-load.
   * Pre-doing just that reparent at idle/intent — while the drawer stays `hidden`
   * (so it's invisible and inert) — lets the open find `parentElement === body`
   * and skip the move: measured ~560ms → ~120ms, no flash, no scroll-lock, no
   * focus-trap. The facet <form> is a descendant of the drawer, so it travels with
   * it as one unit (exactly what the theme relies on during a normal open).
   *
   * Scheduling never piles onto the post-load burst:
   *   • Intent-warm (the real win): first pointerenter/touchstart/focus on the
   *     Filter button → the reparent is synchronous, so even the SAME tap that
   *     opens benefits, and only people about to filter pay it.
   *   • Idle-warm fallback: well after `load`, only if the tab is visible and the
   *     connection isn't data-saver/2g. Uses scheduler.postTask(background) →
   *     requestIdleCallback → setTimeout (the chain matters: iOS Safari has no rIC).
   * The treatment/control split is the sticky A/B bucket (registry `drawer_warm` in
   * snippets/custom-ab-head.liquid, read via window.Experiments) — only `treatment`
   * warms, so `control` users keep the cold path and the two arms are comparable in GA4
   * via the `experiment_exposure` event. Test seam: window.__cxForceWarm === true forces
   * warm on (defeats the bucket), === false forces it off — used by the QA seam
   * below and e2e/performance/facet-drawer-open.spec.ts.
   * ======================================================================= */
  /* QA/preview seam: force the pre-warm on/off for manual testing on a preview theme
   * (no DevTools needed) via `?__cxforcewarm=true|false` on the URL, or a sticky
   * localStorage `cx_warm` = '1'|'0'. Absent → the normal 50/50 holdback applies.
   * The e2e spec drives the same query param. */
  (function () {
    try {
      var m = location.search.match(/[?&]__cxforcewarm=(true|false)/i);
      if (m) {
        window.__cxForceWarm = (m[1].toLowerCase() === 'true');
      } else if (window.localStorage) {
        var ls = localStorage.getItem('cx_warm');
        if (ls === '1') window.__cxForceWarm = true;
        else if (ls === '0') window.__cxForceWarm = false;
      }
    } catch (e) { /* never break the page */ }
  })();

  // Sticky A/B bucket for the drawer-warm experiment (null when the kill-switch is
  // off). `prewarmed` on filter_drawer_open still records the ACTUAL per-open state;
  // this is the assignment (intent-to-treat) reported via `experiment_exposure`.
  var drawerVariant = CONFIG.warmDrawer ? variantFor('drawer_warm') : null;
  var drawerWarmEnabled = (window.__cxForceWarm === true) ||
    (window.__cxForceWarm !== false && drawerVariant === 'treatment');
  var drawerWarmed = false;

  function warmFacetDrawer() {
    if (drawerWarmed) return;
    var drawer = document.getElementById('FacetDrawer');
    if (!drawer || drawer.hasAttribute('open')) return;  // already open/used → nothing to warm
    if (drawer.parentElement === document.body) return;  // already at <body> → reparent done
    drawerWarmed = true;
    try {
      // Move the (still-hidden, position:fixed) drawer to <body> now, so the open's
      // synchronous reparent is already done. Invisible + inert until really opened.
      document.body.appendChild(drawer);
    } catch (e) { /* never break the page */ }
  }

  function scheduleIdleWarm(cb) {
    if (window.scheduler && typeof window.scheduler.postTask === 'function') {
      try { window.scheduler.postTask(cb, { priority: 'background' }); return; } catch (e) { /* fall through */ }
    }
    if (typeof window.requestIdleCallback === 'function') { window.requestIdleCallback(cb, { timeout: 3000 }); return; }
    window.setTimeout(cb, 1500); // iOS Safari path
  }

  var OPENER_SEL = '.facet-drawer-button[aria-controls="FacetDrawer"]';
  var INTENT_EVENTS = ['pointerover', 'touchstart', 'focusin']; // bubbling-capable → delegable

  function setupDrawerWarming() {
    if (!drawerWarmEnabled) return;
    // Design mode (Shopify theme editor) re-renders sections on edit, which would
    // re-emit a #FacetDrawer in the section while our pre-warm has moved the original
    // to <body> → two #FacetDrawer nodes. The pre-warm is a storefront-perf tweak with
    // no value in the editor, so skip it there entirely.
    if (window.Shopify && window.Shopify.designMode) return;
    if (!document.getElementById('FacetDrawer')) return; // no facet drawer on this page

    // Intent-warm via DELEGATION (not bound to a specific button node): a facet
    // re-render can swap the Filter button, which would orphan a once-listener bound to
    // the old node. Delegating on document survives that and any number of buttons;
    // self-removes after the first warm.
    function teardownIntent() {
      INTENT_EVENTS.forEach(function (ev) { document.removeEventListener(ev, onIntent, true); });
    }
    function onIntent(e) {
      if (drawerWarmed) { teardownIntent(); return; }
      var t = e.target;
      if (t && t.closest && t.closest(OPENER_SEL)) { warmFacetDrawer(); teardownIntent(); }
    }
    INTENT_EVENTS.forEach(function (ev) { document.addEventListener(ev, onIntent, { capture: true, passive: true }); });

    // Idle-warm fallback — well after load, only when it's safe/worthwhile.
    var armIdle = function () {
      window.setTimeout(function () {
        if (drawerWarmed) { teardownIntent(); return; }
        if (document.visibilityState !== 'visible') return;
        var c = navigator.connection;
        if (c && (c.saveData || /2g$/.test(c.effectiveType || ''))) return; // intent-warm still covers these
        scheduleIdleWarm(function () { warmFacetDrawer(); teardownIntent(); });
      }, 1200);
    };
    if (document.readyState === 'complete') armIdle();
    else window.addEventListener('load', armIdle, { once: true });
  }

  function init() {
    if (!document.getElementById('ProductsList')) return; // collection pages only

    fireViewItemList('load');
    document.addEventListener('click', onClick, true);
    document.addEventListener('change', onChange, true);
    document.addEventListener('click', onFacetDrawerOpenClick, true);
    setupDrawerWarming();

    // Experiment exposure — the shopper is in the drawer_warm experiment on this
    // collection page (fired for BOTH arms so they're comparable in GA4). Skip in the
    // theme editor so merchant edit sessions don't pollute the experiment.
    if (drawerVariant && document.getElementById('FacetDrawer') && !(window.Shopify && window.Shopify.designMode)) {
      exposeExperiment('drawer_warm');
    }

    checkNoResults();
    var container = document.getElementById('ProductGridContainer') || document.getElementById('ProductsList');
    if (container && 'MutationObserver' in window) {
      var timer = null;
      var obs = new MutationObserver(function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(onGridSettled, CONFIG.refireDebounceMs);
      });
      obs.observe(container, { childList: true, subtree: true });
    }
  }

  /* =========================================================================
   * Experiment side effects that run on EVERY page (not just collections):
   *   • shopify_view stickiness — keep the assigned ?view= suffix on same-template
   *     internal links so the server-rendered variant follows the shopper across
   *     navigation (capture-phase, so dynamically-added links are covered too).
   *   • auto-exposure — fire experiment_exposure for expose:'auto' experiments.
   * Manual-expose experiments (drawer_warm, loadmore_autoadvance) expose from their
   * own surfaces instead. No-op when no experiment needs it.
   * ======================================================================= */
  function setupViewStickiness() {
    if (!Experiments) return;
    var reg = Experiments._registry || {}, assigned = Experiments.assigned(), rules = [];
    for (var id in reg) {
      if (!reg.hasOwnProperty(id) || reg[id].mode !== 'shopify_view' || !assigned.hasOwnProperty(id)) continue;
      var cfg = reg[id], suffix = '';
      for (var i = 0; i < cfg.variants.length; i++) if (cfg.variants[i][0] === assigned[id]) suffix = cfg.variants[i][1];
      var pat = cfg.template === 'collection' ? '/collections/' : cfg.template === 'product' ? '/products/' : null;
      if (suffix && pat) rules.push({ pat: pat, suffix: suffix });
    }
    if (!rules.length) return;
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || (a.host && a.host !== location.host) || /[?&]view=/.test(a.search)) return;
      for (var k = 0; k < rules.length; k++) {
        if (a.pathname.indexOf(rules[k].pat) !== -1) { a.search += (a.search ? '&' : '?') + 'view=' + encodeURIComponent(rules[k].suffix); break; }
      }
    }, true);
  }
  function initExperiments() {
    if (!Experiments) return;
    var reg = Experiments._registry || {}, assigned = Experiments.assigned();
    setupViewStickiness();
    for (var id in reg) {
      if (reg.hasOwnProperty(id) && assigned.hasOwnProperty(id) && reg[id].expose === 'auto') exposeExperiment(id);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); initExperiments(); });
  } else {
    init();
    initExperiments();
  }
})();
